/**
 * InboundSmsPage — Bejovo SMS uzenetek kezelese, feldolgozas
 * Epic 67: SMS Gateway & Ketiranyú Protokoll
 */

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useSmsHistory } from '@/hooks/useSms';
import { useTranslation } from '@kgc/i18n';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

type InboundFilter = 'ALL' | 'PENDING' | 'PROCESSED';

// Recognized command patterns (e.g., "HOSSZABBIT 3 NAP", "KESZ", "LEMONDAS")
const COMMAND_PATTERNS: { pattern: RegExp; key: string }[] = [
  { pattern: /^HOSSZABB[IÍ]T\s+\d+\s+NAP$/i, key: 'sms.commandExtend' },
  { pattern: /^K[EÉ]SZ$/i, key: 'sms.commandReady' },
  { pattern: /^LEMOND[AÁ]S$/i, key: 'sms.commandCancel' },
  { pattern: /^ST[AÁ]TUSZ$/i, key: 'sms.commandStatus' },
];

function detectCommand(message: string): string | null {
  const trimmed = message.trim();
  for (const { pattern, key } of COMMAND_PATTERNS) {
    if (pattern.test(trimmed)) return key;
  }
  return null;
}

const STATUS_BADGE: Record<string, string> = {
  PENDING: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  DELIVERED: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  SENT: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  FAILED: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
};

export function InboundSmsPage() {
  const { t, locale } = useTranslation();
  const LOCALE_MAP: Record<string, string> = { hu: 'hu-HU', en: 'en-GB', de: 'de-DE', uk: 'uk-UA' };
  const navigate = useNavigate();
  const [filter, setFilter] = useState<InboundFilter>('ALL');
  const [processedIds, setProcessedIds] = useState<Set<string>>(new Set());

  const { result: history, isLoading } = useSmsHistory({ direction: 'INBOUND' });

  const filteredLogs = useMemo(() => {
    if (!history?.logs) return [];
    const inbound = history.logs;

    if (filter === 'PENDING') {
      return inbound.filter(log => !processedIds.has(log.id));
    }
    if (filter === 'PROCESSED') {
      return inbound.filter(log => processedIds.has(log.id));
    }
    return inbound;
  }, [history, filter, processedIds]);

  const handleMarkProcessed = (id: string) => {
    setProcessedIds(prev => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">{t('sms.inboundTitle')}</h1>
        <Button variant="outline" onClick={() => navigate('/messaging/sms')}>
          {t('sms.backToDashboard')}
        </Button>
      </div>

      {/* Filter buttons */}
      <div className="flex gap-2 mb-6">
        <Button
          variant={filter === 'ALL' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setFilter('ALL')}
        >
          {t('common.all')}
        </Button>
        <Button
          variant={filter === 'PENDING' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setFilter('PENDING')}
        >
          {t('sms.filterPending')}
        </Button>
        <Button
          variant={filter === 'PROCESSED' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setFilter('PROCESSED')}
        >
          {t('sms.filterProcessed')}
        </Button>
      </div>

      {/* Inbound list */}
      {isLoading ? (
        <div className="text-center py-8 text-gray-500 dark:text-[#ccfbf1]">
          {t('common.loading')}
        </div>
      ) : filteredLogs.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-gray-500 dark:text-[#ccfbf1]">
            {t('sms.noInboundMessages')}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredLogs.map(log => {
            const commandKey = detectCommand(log.message);
            const isProcessed = processedIds.has(log.id);

            return (
              <Card key={log.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-1">
                        <span className="font-medium">{log.phoneNumber}</span>
                        <span
                          className={`px-2 py-0.5 rounded text-xs font-medium ${
                            isProcessed
                              ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                              : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
                          }`}
                        >
                          {isProcessed ? t('sms.statusProcessed') : t('sms.statusPending')}
                        </span>
                        {log.status && (
                          <span
                            className={`px-2 py-0.5 rounded text-xs ${STATUS_BADGE[log.status] ?? ''}`}
                          >
                            {log.status}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-700 dark:text-gray-300 mb-1">{log.message}</p>
                      {commandKey && (
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 px-2 py-0.5 rounded">
                            {t('sms.recognizedCommand')}: {t(commandKey)}
                          </span>
                        </div>
                      )}
                      <div className="text-xs text-gray-400 dark:text-[#99f6e4]">
                        {new Date(log.createdAt).toLocaleString(LOCALE_MAP[locale] ?? 'hu-HU')}
                      </div>
                    </div>
                    <div className="flex gap-2 ml-4 flex-shrink-0">
                      {!isProcessed && (
                        <Button size="sm" onClick={() => handleMarkProcessed(log.id)}>
                          {t('sms.markProcessed')}
                        </Button>
                      )}
                      {commandKey && (
                        <Button variant="outline" size="sm" onClick={() => navigate('/worksheet')}>
                          {t('sms.viewRelated')}
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
